'use client'

import {
  AttachFile,
  ErrorOutline,
  ImageOutlined,
  InsertDriveFileOutlined,
  PictureAsPdfOutlined,
  TableChartOutlined
} from '@mui/icons-material'
import { Avatar, Box, Chip, CircularProgress, IconButton, Stack, Tooltip } from '@mui/material'
import { useTranslations } from 'next-intl'
import { type ChangeEvent, type ReactElement, forwardRef, useImperativeHandle, useRef } from 'react'

import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_COUNT,
  type MessageAttachment,
  type PendingAttachment,
  formatAttachmentSize,
  isImageAttachment
} from './attachments'

type PendingAttachmentChipsProps = {
  attachments: PendingAttachment[]
  onRemove: (localId: string) => void
  disabled?: boolean
}

/** The chips above a composer input: filename + size, upload progress, error state and a remove action. */
export function PendingAttachmentChips({ attachments, onRemove, disabled }: PendingAttachmentChipsProps) {
  const t = useTranslations('features.AssistantAttachments')
  if (attachments.length === 0) return null

  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" data-cy="assistant-attachment-chips">
      {attachments.map((entry) => {
        const label = `${entry.file.name} · ${formatAttachmentSize(entry.file.size)}`
        const errorText = entry.error ? t(`error_${entry.error}`) : undefined
        return (
          <Tooltip key={entry.localId} title={errorText ?? (entry.status === 'uploading' ? t('uploading') : label)}>
            <Chip
              size="small"
              color={entry.status === 'error' ? 'error' : 'default'}
              variant={entry.status === 'ready' ? 'filled' : 'outlined'}
              icon={
                entry.status === 'uploading' ? (
                  <CircularProgress size={14} variant={entry.progress > 0 ? 'determinate' : 'indeterminate'} value={entry.progress} />
                ) : entry.status === 'error' ? (
                  <ErrorOutline fontSize="small" />
                ) : (
                  fileIcon(entry.file.type || '', entry.file.name)
                )
              }
              label={entry.status === 'error' && errorText ? `${entry.file.name} – ${errorText}` : label}
              onDelete={disabled ? undefined : () => onRemove(entry.localId)}
              aria-label={entry.status === 'error' ? `${entry.file.name}: ${errorText ?? ''}` : label}
              data-cy="assistant-attachment-chip"
              data-status={entry.status}
              sx={{ maxWidth: '100%' }}
            />
          </Tooltip>
        )
      })}
    </Stack>
  )
}

type MessageAttachmentChipsProps = {
  attachments: MessageAttachment[]
}

/** Read-only chips under a sent user bubble; local images show a thumbnail. */
export function MessageAttachmentChips({ attachments }: MessageAttachmentChipsProps) {
  if (attachments.length === 0) return null
  return (
    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" justifyContent="flex-end" sx={{ mt: 0.5, maxWidth: '85%' }}>
      {attachments.map((attachment) => (
        <Chip
          key={attachment.attachmentId}
          size="small"
          variant="outlined"
          avatar={
            attachment.previewUrl ? (
              <Avatar src={attachment.previewUrl} alt="" variant="rounded" />
            ) : undefined
          }
          icon={attachment.previewUrl ? undefined : fileIcon(attachment.contentType, attachment.filename)}
          label={`${attachment.filename} · ${formatAttachmentSize(attachment.sizeBytes)}`}
          data-cy="assistant-message-attachment"
          sx={{ maxWidth: '100%' }}
        />
      ))}
    </Stack>
  )
}

type AttachFileButtonProps = {
  onFiles: (files: FileList) => void
  disabled?: boolean
  size?: 'small' | 'medium'
}

export type AttachFileButtonHandle = {
  /** Opens the native file picker, e.g. from a starter prompt that needs a document first. */
  openPicker: () => void
}

/** Paperclip button wrapping a hidden multi-file input restricted to the allowed attachment types. */
export const AttachFileButton = forwardRef<AttachFileButtonHandle, AttachFileButtonProps>(function AttachFileButton(
  { onFiles, disabled, size = 'small' },
  ref
) {
  const t = useTranslations('features.AssistantAttachments')
  const inputRef = useRef<HTMLInputElement>(null)
  useImperativeHandle(ref, () => ({ openPicker: () => inputRef.current?.click() }), [])

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      onFiles(event.target.files)
    }
    // Reset so picking the same file again re-fires change.
    event.target.value = ''
  }

  return (
    <Box component="span">
      <Tooltip title={t('attach_tooltip', { max: ATTACHMENT_MAX_COUNT })}>
        <span>
          <IconButton
            size={size}
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
            aria-label={t('attach_label')}
            data-cy="assistant-attach-button"
          >
            <AttachFile fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        onChange={onChange}
        hidden
        data-cy="assistant-attach-input"
      />
    </Box>
  )
})

function fileIcon(contentType: string, filename: string): ReactElement {
  const lower = filename.toLowerCase()
  if (contentType === 'application/pdf' || lower.endsWith('.pdf')) return <PictureAsPdfOutlined fontSize="small" />
  if (isImageAttachment(contentType)) return <ImageOutlined fontSize="small" />
  if (contentType === 'text/csv' || lower.endsWith('.csv') || lower.endsWith('.xlsx')) {
    return <TableChartOutlined fontSize="small" />
  }
  return <InsertDriveFileOutlined fontSize="small" />
}
